/**
 * Per-route SEO copy (title + description) for STATIC pages, in every locale.
 *
 * Centralising this keeps every route file a one-liner and makes the page-level
 * SEO a single, reviewable table. Dynamic detail pages (lesson, course, ...)
 * derive their title/description from the localized catalog instead and so are
 * not listed here.
 *
 * The German copy is the existing, SEO-tuned wording ported out of the route
 * files; the English copy is native marketing copy for the new default locale.
 */
import type { Locale } from './config'
import type { RouteKey } from './routes'

export type RouteSeo = { title: string; description: string; ogImage?: string }

type StaticSeoKey = Extract<
  RouteKey,
  | 'home'
  | 'courses'
  | 'knowledge'
  | 'fundamentals'
  | 'builds'
  | 'glossary'
  | 'compare'
  | 'guides'
  | 'tools'
  | 'about'
  | 'pricing'
  | 'resources'
  | 'community'
  | 'communityTopics'
  | 'search'
  | 'changelog'
  | 'newsletter'
  | 'contact'
  | 'terms'
  | 'imprint'
  | 'privacy'
  | 'accessibility'
  | 'cookies'
  | 'apiDocs'
>

/**
 * Every shipped locale MUST supply its own SEO copy. Making this a full
 * `Record<Locale, RouteSeo>` turns a missing translation into a compile error
 * rather than a silent English fallback in a foreign-language SERP.
 */
type LocalizedRouteSeo = Record<Locale, RouteSeo>

export const ROUTE_SEO: Record<StaticSeoKey, LocalizedRouteSeo> = {
  home: {
    en: {
      title: 'Learn Agentic Engineering: AI Agents & Real ROI',
      description:
        'Learn agentic engineering: AI agents, Claude Code and Codex for measurable ROI. Start free with Courses 1-2 and every guide, go deeper with Pro.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Agentic Engineering lernen: KI Agents & messbarer ROI',
      description:
        'Lerne Agentic Engineering auf Deutsch: KI Agents, Claude Code und Codex für messbaren ROI. Kurs 1-2 und alle Guides gratis, mit Pro noch tiefer.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: "Apprendre l'agentic engineering : agents IA et vrai ROI",
      description:
        "Apprenez l'agentic engineering : agents IA, Claude Code et Codex pour un ROI mesurable. Cours 1-2 et tous les guides gratuits, Pro pour aller plus loin.",
      ogImage: '/og/home.png',
    },
    it: {
      title: "Imparare l'agentic engineering: agenti IA e ROI reale",
      description:
        "Impara l'agentic engineering: agenti IA, Claude Code e Codex per un ROI misurabile. Corsi 1-2 e tutte le guide gratis, con Pro vai oltre.",
      ogImage: '/og/home.png',
    },
  },
  courses: {
    en: {
      title: 'Courses - Agentic Engineering | Agentic School',
      description:
        'Every course on AI agents, Claude Code, Codex, APIs and automation, with a business-ROI focus. Courses 1-2 are free, Courses 3-5 are Pro.',
      ogImage: '/og/courses.png',
    },
    de: {
      title: 'Kurse - Agentic Engineering | Agentic School',
      description:
        'Alle Kurse zu KI Agents, Claude Code, Codex, APIs und Automationen, mit Business-ROI-Fokus. Kurs 1-2 sind gratis, Kurs 3-5 sind Pro.',
      ogImage: '/og/courses.png',
    },
    fr: {
      title: 'Cours - Agentic Engineering | Agentic School',
      description:
        'Tous les cours sur les agents IA, Claude Code, Codex, les API et les automatisations, avec un focus ROI business. Cours 1-2 gratuits, cours 3-5 en Pro.',
      ogImage: '/og/courses.png',
    },
    it: {
      title: 'Corsi - Agentic Engineering | Agentic School',
      description:
        'Tutti i corsi su agenti IA, Claude Code, Codex, API e automazioni, con focus sul ROI di business. Corsi 1-2 gratis, corsi 3-5 in Pro.',
      ogImage: '/og/courses.png',
    },
  },
  knowledge: {
    en: {
      title: 'Knowledge: Agentic Engineering Guides | ABA',
      description:
        'Cornerstone guides on agentic engineering: definitions, tool comparisons, ROI and workflows for your business. Practical, grounded and free.',
      ogImage: '/og/wissen.png',
    },
    de: {
      title: 'Wissen: Agentic Engineering Guides | ABA',
      description:
        'Cornerstone-Guides zu Agentic Engineering: Definitionen, Tool-Vergleiche, ROI und Workflows für dein Unternehmen. Praxisnah, fundiert und kostenlos.',
      ogImage: '/og/wissen.png',
    },
    fr: {
      title: "Savoir : guides d'agentic engineering | ABA",
      description:
        "Guides de référence sur l'agentic engineering : définitions, comparatifs d'outils, ROI et workflows pour votre entreprise. Concret, fiable et gratuit.",
      ogImage: '/og/wissen.png',
    },
    it: {
      title: "Sapere: guide di agentic engineering | ABA",
      description:
        "Guide cardine sull'agentic engineering: definizioni, confronti tra strumenti, ROI e workflow per la tua azienda. Pratico, solido e gratuito.",
      ogImage: '/og/wissen.png',
    },
  },
  fundamentals: {
    en: {
      title: 'Fundamentals: Beginner Tech Basics | Agentic School',
      description:
        'Plain-language basics for building with AI: Node.js, the terminal, Git, APIs, .env files, DNS and more. Clear definitions and quick setup steps, free.',
      ogImage: '/og/wissen.png',
    },
    de: {
      title: 'Grundlagen: Tech-Basics für Anfänger | Agentic School',
      description:
        'Tech-Basics in klarer Sprache fürs Bauen mit KI: Node.js, Terminal, Git, APIs, .env, DNS und mehr. Klare Definitionen und schnelle Setup-Schritte, kostenlos.',
      ogImage: '/og/wissen.png',
    },
    fr: {
      title: 'Fondamentaux : bases techniques pour débutants | Agentic School',
      description:
        "Bases techniques en langage clair pour construire avec l'IA : Node.js, terminal, Git, API, .env, DNS et plus. Définitions claires et setup rapide, gratuit.",
      ogImage: '/og/wissen.png',
    },
    it: {
      title: 'Fondamenti: basi tecniche per principianti | Agentic School',
      description:
        "Basi tecniche in linguaggio chiaro per costruire con l'IA: Node.js, terminale, Git, API, .env, DNS e altro. Definizioni chiare e setup rapido, gratis.",
      ogImage: '/og/wissen.png',
    },
  },
  builds: {
    en: {
      title: 'Builds: Founder Project Case Studies | ABA',
      description:
        'Honest case studies from 20+ shipped products: API-first tools, automations, image-to-data pipelines and voice agents. Real lessons, no vanity metrics.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Projekte: Gründer-Case-Studies | Agentic School',
      description:
        'Ehrliche Case Studies aus 20+ Produkten: API-First Tools, Automationen, Bild-zu-Daten-Pipelines und Voice-Agents. Echte Lektionen, keine Vanity-Metriken.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: 'Projets : études de cas de fondateur | ABA',
      description:
        "Études de cas honnêtes de 20+ produits livrés : outils API-first, automatisations, pipelines image-vers-données et agents vocaux. Vraies leçons, sans esbroufe.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Progetti: case study da founder | Agentic School',
      description:
        "Case study oneste da oltre 20 prodotti spediti: strumenti API-first, automazioni, pipeline da immagine a dati e agenti vocali. Lezioni vere, niente vanity metric.",
      ogImage: '/og/home.png',
    },
  },
  glossary: {
    en: {
      title: 'AI Glossary: Agent Terms Explained | ABA',
      description:
        'Plain-language definitions of AI and agent vocabulary: AI agent, agentic AI, MCP, vibe coding, context window and more. Each term clearly answered, free.',
      ogImage: '/og/wissen.png',
    },
    de: {
      title: 'KI Glossar: Agent-Begriffe erklärt | ABA',
      description:
        'Klare Definitionen des KI- und Agent-Vokabulars: KI Agent, Agentic AI, MCP, Vibe Coding, Context Window und mehr. Jeder Begriff verständlich erklärt.',
      ogImage: '/og/wissen.png',
    },
    fr: {
      title: 'Glossaire IA : les termes des agents expliqués | ABA',
      description:
        "Définitions en langage clair du vocabulaire IA et agents : agent IA, IA agentique, MCP, vibe coding, fenêtre de contexte et plus. Chaque terme expliqué.",
      ogImage: '/og/wissen.png',
    },
    it: {
      title: 'Glossario IA: i termini degli agenti spiegati | ABA',
      description:
        "Definizioni in linguaggio chiaro del vocabolario IA e agenti: agente IA, IA agentica, MCP, vibe coding, finestra di contesto e altro. Ogni termine spiegato.",
      ogImage: '/og/wissen.png',
    },
  },
  compare: {
    en: {
      title: 'Compare AI Coding Tools and Stacks | ABA',
      description:
        'Honest, current comparisons of AI coding tools, agents, models and the modern app stack. Spec tables, pros and cons and a clear verdict for each. Free.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'KI Coding Tools und Stacks vergleichen | ABA',
      description:
        'Ehrliche, aktuelle Vergleiche von KI Coding Tools, Agents, Modellen und dem modernen App-Stack. Spec-Tabellen, Pro und Contra und ein klares Fazit. Kostenlos.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: "Comparer les outils et stacks de coding IA | ABA",
      description:
        "Comparatifs honnêtes et à jour des outils de coding IA, agents, modèles et de la stack applicative moderne. Tableaux de specs, pour et contre et verdict clair.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Confrontare strumenti e stack di coding IA | ABA',
      description:
        "Confronti onesti e aggiornati di strumenti di coding IA, agenti, modelli e dello stack applicativo moderno. Tabelle di spec, pro e contro e un verdetto chiaro.",
      ogImage: '/og/home.png',
    },
  },
  guides: {
    en: {
      title: 'Guides: Agentic Engineering How-Tos | ABA',
      description:
        'Practical guides on Claude Code, agents, MCP, the modern stack and AI automation. Step-by-step how-tos and pillar guides for builders. Hands-on and free.',
      ogImage: '/og/wissen.png',
    },
    de: {
      title: 'Ratgeber: Agentic Engineering How-Tos | ABA',
      description:
        'Praktische Ratgeber zu Claude Code, Agents, MCP, dem modernen Stack und KI Automation. Schritt-für-Schritt-Anleitungen und Pillar-Guides für Builder.',
      ogImage: '/og/wissen.png',
    },
    fr: {
      title: "Guides : how-tos d'agentic engineering | ABA",
      description:
        "Guides pratiques sur Claude Code, les agents, MCP, la stack moderne et l'automatisation IA. Tutoriels pas à pas et guides piliers pour les builders. Gratuit.",
      ogImage: '/og/wissen.png',
    },
    it: {
      title: 'Guide: how-to di agentic engineering | ABA',
      description:
        "Guide pratiche su Claude Code, agenti, MCP, lo stack moderno e l'automazione IA. Tutorial passo dopo passo e guide pilastro per i builder. Pratico e gratis.",
      ogImage: '/og/wissen.png',
    },
  },
  tools: {
    en: {
      title: 'Free AI Tools: Calculators and Generators | ABA',
      description:
        'Free, no-signup interactive tools for builders: an llms.txt generator plus cost, token and decision helpers. All in your browser, private and free.',
      ogImage: '/og/resources.png',
    },
    de: {
      title: 'Kostenlose KI Tools: Rechner und Generatoren | ABA',
      description:
        'Kostenlose Tools ohne Anmeldung für Builder: ein llms.txt Generator plus Kosten-, Token- und Entscheidungshelfer. Komplett im Browser, privat und kostenlos.',
      ogImage: '/og/resources.png',
    },
    fr: {
      title: 'Outils IA gratuits : calculateurs et générateurs | ABA',
      description:
        "Outils interactifs gratuits et sans inscription pour builders : générateur llms.txt et assistants de coût, de tokens et de décision. Dans le navigateur, privé.",
      ogImage: '/og/resources.png',
    },
    it: {
      title: 'Strumenti IA gratuiti: calcolatori e generatori | ABA',
      description:
        "Strumenti interattivi gratuiti e senza registrazione per builder: generatore llms.txt e assistenti di costo, token e decisione. Nel browser, privati e gratis.",
      ogImage: '/og/resources.png',
    },
  },
  pricing: {
    en: {
      title: 'Pricing: Free Courses + Agentic School Pro | Agentic School',
      description:
        'Start free with Courses 1-2 and every guide. Agentic School Pro unlocks Courses 3-5 (auth, payments, automation, security) from $29/month. 30-day money-back.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Preise: Gratis-Kurse + Agentic School Pro | Agentic School',
      description:
        'Starte gratis mit Kurs 1-2 und allen Ratgebern. Agentic School Pro schaltet Kurs 3-5 frei (Auth, Payments, Automatisierung, Sicherheit) ab $29/Monat.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: 'Tarifs : cours gratuits + Agentic School Pro | Agentic School',
      description:
        'Commencez gratuitement avec les cours 1-2 et tous les guides. Agentic School Pro débloque les cours 3-5 (auth, paiements, automatisation, sécurité) dès 29 $/mois.',
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Prezzi: corsi gratuiti + Agentic School Pro | Agentic School',
      description:
        'Inizia gratis con i corsi 1-2 e tutte le guide. Agentic School Pro sblocca i corsi 3-5 (auth, pagamenti, automazione, sicurezza) da 29 $/mese.',
      ogImage: '/og/home.png',
    },
  },
  about: {
    en: {
      title: 'About the Founder | Agentic School',
      description:
        'Meet Nico, the founder of Agentic School. 20+ products shipped with AI agents, an agent-first philosophy, and the honest workflow behind every lesson here.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Über den Gründer | Agentic School',
      description:
        'Lerne Nico kennen, den Gründer der Agentic School: 20+ Produkte mit KI Agents gebaut, eine Agent-First-Philosophie und der ehrliche Workflow jeder Lektion.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: 'À propos du fondateur | Agentic School',
      description:
        "Découvrez Nico, le fondateur d'Agentic School : 20+ produits livrés avec des agents IA, une philosophie agent-first et le workflow honnête de chaque leçon.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Il fondatore | Agentic School',
      description:
        "Conosci Nico, il fondatore di Agentic School: oltre 20 prodotti creati con agenti IA, una filosofia agent-first e il workflow onesto dietro ogni lezione.",
      ogImage: '/og/home.png',
    },
  },
  resources: {
    en: {
      title: 'Resources for AI Workflows | Agentic School',
      description:
        'Curated tools, APIs, prompts, skills and checklists for productive AI workflows in business. Field-tested, ready to use and free.',
      ogImage: '/og/resources.png',
    },
    de: {
      title: 'Ressourcen für KI Workflows | Agentic School',
      description:
        'Kuratierte Tools, APIs, Prompts, Skills und Checklisten für produktive KI Workflows im Unternehmen. Praxiserprobt, sofort einsetzbar und kostenlos.',
      ogImage: '/og/resources.png',
    },
    fr: {
      title: 'Ressources pour workflows IA | Agentic School',
      description:
        "Outils, API, prompts, skills et checklists sélectionnés pour des workflows IA productifs en entreprise. Éprouvés sur le terrain, prêts à l'emploi et gratuits.",
      ogImage: '/og/resources.png',
    },
    it: {
      title: 'Risorse per workflow IA | Agentic School',
      description:
        "Strumenti, API, prompt, skill e checklist selezionati per workflow IA produttivi in azienda. Testati sul campo, pronti all'uso e gratuiti.",
      ogImage: '/og/resources.png',
    },
  },
  community: {
    en: {
      title: 'Community - Agentic Engineering | Agentic School',
      description:
        'Ask questions, share showcases and get workflow reviews around AI agents and agentic engineering. Learn with others - open and free.',
      ogImage: '/og/community.png',
    },
    de: {
      title: 'Community - Agentic Engineering | Agentic School',
      description:
        'Stelle Fragen, teile Showcases und hol dir Workflow Reviews rund um KI Agents und Agentic Engineering. Lern mit anderen - offen und kostenlos.',
      ogImage: '/og/community.png',
    },
    fr: {
      title: 'Communauté - Agentic Engineering | Agentic School',
      description:
        "Posez des questions, partagez vos réalisations et obtenez des revues de workflow autour des agents IA et de l'agentic engineering. Apprenez ensemble, gratuit.",
      ogImage: '/og/community.png',
    },
    it: {
      title: 'Community - Agentic Engineering | Agentic School',
      description:
        "Fai domande, condividi i tuoi showcase e ottieni revisioni di workflow su agenti IA e agentic engineering. Impara con gli altri - aperto e gratuito.",
      ogImage: '/og/community.png',
    },
  },
  communityTopics: {
    en: {
      title: 'Community Topics | Agentic School',
      description:
        'Every community space: questions, workflow reviews, showcases and tool updates around AI agents and agentic engineering. Find your topic - free.',
      ogImage: '/og/community.png',
    },
    de: {
      title: 'Community-Themen | Agentic School',
      description:
        'Alle Community-Bereiche: Fragen, Workflow Reviews, Showcases und Tool-Updates rund um KI Agents und Agentic Engineering. Finde dein Thema - kostenlos.',
      ogImage: '/og/community.png',
    },
    fr: {
      title: 'Thèmes de la communauté | Agentic School',
      description:
        "Tous les espaces communautaires : questions, revues de workflow, réalisations et updates d'outils autour des agents IA et de l'agentic engineering. Gratuit.",
      ogImage: '/og/community.png',
    },
    it: {
      title: 'Temi della community | Agentic School',
      description:
        "Tutti gli spazi della community: domande, revisioni di workflow, showcase e aggiornamenti sugli strumenti di agenti IA e agentic engineering. Trova il tuo tema.",
      ogImage: '/og/community.png',
    },
  },
  search: {
    en: {
      title: 'Search | Agentic School',
      description:
        'Search the courses, lessons, resources, changelog and community of Agentic School. Find the AI workflow knowledge you need in seconds.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Suche | Agentic School',
      description:
        'Durchsuche Kurse, Lektionen, Ressourcen, Changelog und Community der Agentic School. Finde in Sekunden das passende KI Workflow Wissen.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: 'Recherche | Agentic School',
      description:
        "Recherchez dans les cours, leçons, ressources, changelog et communauté d'Agentic School. Trouvez en quelques secondes le savoir workflow IA qu'il vous faut.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Ricerca | Agentic School',
      description:
        "Cerca tra corsi, lezioni, risorse, changelog e community di Agentic School. Trova in pochi secondi la conoscenza sui workflow IA che ti serve.",
      ogImage: '/og/home.png',
    },
  },
  changelog: {
    en: {
      title: 'Changelog - AI Tool Updates | Agentic School',
      description:
        'Daily-relevant updates to Claude Code, Codex and the wider agentic tooling, with what changed and why it matters for your workflows. Free.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Changelog - KI Tool Updates | Agentic School',
      description:
        'Tagesaktuelle Updates zu Claude Code, Codex und dem Agentic-Tooling, mit was sich geändert hat und warum es für deine Workflows zählt. Kostenlos.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: "Changelog - updates des outils IA | Agentic School",
      description:
        "Updates quotidiennement pertinents de Claude Code, Codex et de l'outillage agentique, avec ce qui a changé et pourquoi cela compte pour vos workflows. Gratuit.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Changelog - aggiornamenti strumenti IA | Agentic School',
      description:
        "Aggiornamenti sempre attuali di Claude Code, Codex e del tooling agentico, con cosa è cambiato e perché conta per i tuoi workflow. Gratis.",
      ogImage: '/og/home.png',
    },
  },
  newsletter: {
    en: {
      title: 'Newsletter - AI Workflows | Agentic School',
      description:
        'New lessons, tool updates, API ideas and concrete agentic engineering examples - once a week, straight to your inbox. Free, unsubscribe anytime.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Newsletter - KI Workflows | Agentic School',
      description:
        'Neue Lektionen, Tool-Updates, API-Ideen und konkrete Agentic Engineering Beispiele - einmal pro Woche direkt ins Postfach. Kostenlos, jederzeit kündbar.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: 'Newsletter - workflows IA | Agentic School',
      description:
        "Nouvelles leçons, updates d'outils, idées d'API et exemples concrets d'agentic engineering - une fois par semaine dans votre boîte. Gratuit, désabonnement libre.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Newsletter - workflow IA | Agentic School',
      description:
        "Nuove lezioni, aggiornamenti sugli strumenti, idee di API ed esempi concreti di agentic engineering - una volta a settimana nella tua casella. Gratis, disdici quando vuoi.",
      ogImage: '/og/home.png',
    },
  },
  contact: {
    en: {
      title: 'Contact | Agentic School',
      description:
        'Questions about the campus, workflow reviews or partnerships? Write to us about agentic engineering in your business - we reply personally and fast.',
      ogImage: '/og/home.png',
    },
    de: {
      title: 'Kontakt | Agentic School',
      description:
        'Fragen zum Campus, Workflow Reviews oder Kooperationen? Schreib uns zu Agentic Engineering in deinem Unternehmen - wir antworten persönlich und schnell.',
      ogImage: '/og/home.png',
    },
    fr: {
      title: 'Contact | Agentic School',
      description:
        "Des questions sur le campus, des revues de workflow ou des partenariats ? Écrivez-nous sur l'agentic engineering dans votre entreprise - réponse perso et rapide.",
      ogImage: '/og/home.png',
    },
    it: {
      title: 'Contatti | Agentic School',
      description:
        "Domande sul campus, revisioni di workflow o partnership? Scrivici sull'agentic engineering nella tua azienda - rispondiamo di persona e in fretta.",
      ogImage: '/og/home.png',
    },
  },
  terms: {
    en: {
      title: 'Terms | Agentic School',
      description:
        'The terms of use for Agentic School, explained transparently: rights, obligations and the conditions around using the campus.',
    },
    de: {
      title: 'AGB | Agentic School',
      description:
        'Allgemeine Geschäftsbedingungen für die Nutzung der Agentic School. Transparent erklärt: Rechte, Pflichten und Bedingungen rund um den Campus.',
    },
    fr: {
      title: "Conditions | Agentic School",
      description:
        "Les conditions d'utilisation d'Agentic School, expliquées en toute transparence : droits, obligations et conditions liées à l'usage du campus.",
    },
    it: {
      title: 'Termini | Agentic School',
      description:
        "I termini di utilizzo di Agentic School, spiegati in modo trasparente: diritti, obblighi e condizioni relative all'uso del campus.",
    },
  },
  imprint: {
    en: {
      title: 'Imprint | Agentic School',
      description:
        'Legal information and provider details for Agentic School, in line with disclosure requirements. Contact, responsibility and address.',
    },
    de: {
      title: 'Impressum | Agentic School',
      description:
        'Anbieterkennzeichnung und rechtliche Angaben zur Agentic School gemäss Offenlegungspflichten. Kontakt, Verantwortung und Anschrift.',
    },
    fr: {
      title: 'Mentions légales | Agentic School',
      description:
        "Informations légales et coordonnées de l'éditeur d'Agentic School, conformément aux obligations de divulgation. Contact, responsabilité et adresse.",
    },
    it: {
      title: 'Note legali | Agentic School',
      description:
        "Informazioni legali e dati del fornitore di Agentic School, in linea con gli obblighi di trasparenza. Contatto, responsabilità e indirizzo.",
    },
  },
  privacy: {
    en: {
      title: 'Privacy | Agentic School',
      description:
        'How Agentic School handles your data: GDPR, Swiss FADP and US privacy laws, processors, your rights and how to exercise them. Transparent and complete.',
    },
    de: {
      title: 'Datenschutz | Agentic School',
      description:
        'Wie die Agentic School mit deinen Daten umgeht: DSGVO, revFADP und US-Datenschutz, Auftragsverarbeiter, deine Rechte und wie du sie wahrnimmst.',
    },
    fr: {
      title: 'Confidentialité | Agentic School',
      description:
        "Comment Agentic School traite vos données : RGPD, nLPD suisse et lois US, sous-traitants, vos droits et comment les exercer. Transparent et complet.",
    },
    it: {
      title: 'Privacy | Agentic School',
      description:
        "Come Agentic School tratta i tuoi dati: GDPR, revFADP svizzera e leggi USA, responsabili, i tuoi diritti e come esercitarli. Trasparente e completo.",
    },
  },
  accessibility: {
    en: {
      title: 'Accessibility | Agentic School',
      description:
        'Our accessibility commitment for Agentic School: standards we follow, known limitations and how to report a barrier so we can fix it fast.',
    },
    de: {
      title: 'Barrierefreiheit | Agentic School',
      description:
        'Unsere Selbstverpflichtung zur Barrierefreiheit der Agentic School: Standards, bekannte Einschränkungen und wie du eine Barriere meldest.',
    },
    fr: {
      title: 'Accessibilité | Agentic School',
      description:
        "Notre engagement d'accessibilité pour Agentic School : les standards suivis, les limites connues et comment signaler un obstacle pour qu'on le corrige vite.",
    },
    it: {
      title: 'Accessibilità | Agentic School',
      description:
        "Il nostro impegno per l'accessibilità di Agentic School: gli standard che seguiamo, i limiti noti e come segnalare una barriera così da correggerla in fretta.",
    },
  },
  cookies: {
    en: {
      title: 'Cookie settings | Agentic School',
      description:
        'Review and change your cookie and consent choices for Agentic School at any time. Necessary, analytics and external media, fully under your control.',
    },
    de: {
      title: 'Cookie-Einstellungen | Agentic School',
      description:
        'Prüfe und ändere jederzeit deine Cookie- und Einwilligungs-Entscheidungen für die Agentic School. Notwendig, Analytics und externe Medien - selbstbestimmt.',
    },
    fr: {
      title: 'Paramètres des cookies | Agentic School',
      description:
        "Consultez et modifiez à tout moment vos choix de cookies et de consentement pour Agentic School. Nécessaires, analytics et médias externes, sous votre contrôle.",
    },
    it: {
      title: 'Impostazioni cookie | Agentic School',
      description:
        "Consulta e modifica in ogni momento le tue scelte su cookie e consenso per Agentic School. Necessari, analytics e media esterni, sotto il tuo pieno controllo.",
    },
  },
  apiDocs: {
    en: {
      title: 'API and agent access | Agentic School',
      description:
        'Free, read-only JSON API, an OpenAPI 3.1 spec, llms.txt and a Markdown twin for every page. Everything an AI agent needs to consume Agentic School.',
    },
    de: {
      title: 'API und Agent-Zugriff | Agentic School',
      description:
        'Kostenlose read-only JSON-API, eine OpenAPI-3.1-Spezifikation, llms.txt und ein Markdown-Zwilling für jede Seite. Alles, was ein KI Agent braucht.',
    },
    fr: {
      title: "API et accès agent | Agentic School",
      description:
        "API JSON gratuite en lecture seule, une spec OpenAPI 3.1, llms.txt et un jumeau Markdown pour chaque page. Tout ce qu'un agent IA doit consommer d'Agentic School.",
    },
    it: {
      title: 'API e accesso agent | Agentic School',
      description:
        "API JSON gratuita in sola lettura, una spec OpenAPI 3.1, llms.txt e un gemello Markdown per ogni pagina. Tutto ciò che un agente IA deve consumare di Agentic School.",
    },
  },
}

export function routeSeo(key: StaticSeoKey, locale: Locale): RouteSeo {
  // Every locale is required by the type, so this is always a direct hit.
  return ROUTE_SEO[key][locale]
}

/**
 * Titles for noindex (private/auth/account/admin) routes per locale. These
 * pages carry `noindex, nofollow` so they need only a clean, localized title.
 */
type NoIndexKey = Extract<
  RouteKey,
  | 'login'
  | 'register'
  | 'onboarding'
  | 'progress'
  | 'saved'
  | 'admin'
  | 'adminSection'
  | 'settings'
  | 'settingsProfile'
  | 'settingsNotifications'
  | 'settingsPrivacy'
  | 'settingsBilling'
  | 'emailConfirm'
  | 'emailUnsubscribe'
>

type LocalizedTitle = Record<Locale, string>

export const NOINDEX_TITLES: Record<NoIndexKey, LocalizedTitle> = {
  login: {
    en: 'Log in - Agentic School',
    de: 'Login - Agentic School',
    fr: 'Connexion - Agentic School',
    it: 'Accedi - Agentic School',
  },
  register: {
    en: 'Create account - Agentic School',
    de: 'Konto erstellen - Agentic School',
    fr: 'Créer un compte - Agentic School',
    it: 'Crea account - Agentic School',
  },
  onboarding: {
    en: 'Onboarding - Agentic School',
    de: 'Onboarding - Agentic School',
    fr: 'Onboarding - Agentic School',
    it: 'Onboarding - Agentic School',
  },
  progress: {
    en: 'Progress - Agentic School',
    de: 'Fortschritt - Agentic School',
    fr: 'Progression - Agentic School',
    it: 'Progressi - Agentic School',
  },
  saved: {
    en: 'Saved - Agentic School',
    de: 'Gespeichert - Agentic School',
    fr: 'Enregistrés - Agentic School',
    it: 'Salvati - Agentic School',
  },
  admin: {
    en: 'Admin - Agentic School',
    de: 'Admin - Agentic School',
    fr: 'Admin - Agentic School',
    it: 'Admin - Agentic School',
  },
  adminSection: {
    en: 'Admin - Agentic School',
    de: 'Admin - Agentic School',
    fr: 'Admin - Agentic School',
    it: 'Admin - Agentic School',
  },
  settings: {
    en: 'Settings - Agentic School',
    de: 'Einstellungen - Agentic School',
    fr: 'Paramètres - Agentic School',
    it: 'Impostazioni - Agentic School',
  },
  settingsProfile: {
    en: 'Profile - Agentic School',
    de: 'Profil - Agentic School',
    fr: 'Profil - Agentic School',
    it: 'Profilo - Agentic School',
  },
  settingsNotifications: {
    en: 'Notifications - Agentic School',
    de: 'Benachrichtigungen - Agentic School',
    fr: 'Notifications - Agentic School',
    it: 'Notifiche - Agentic School',
  },
  settingsPrivacy: {
    en: 'Privacy settings - Agentic School',
    de: 'Datenschutz-Einstellungen - Agentic School',
    fr: 'Paramètres de confidentialité - Agentic School',
    it: 'Impostazioni privacy - Agentic School',
  },
  settingsBilling: {
    en: 'Billing - Agentic School',
    de: 'Abrechnung - Agentic School',
    fr: 'Facturation - Agentic School',
    it: 'Abbonamento - Agentic School',
  },
  emailConfirm: {
    en: 'Confirm your email - Agentic School',
    de: 'E-Mail bestätigen - Agentic School',
    fr: 'Confirmer votre e-mail - Agentic School',
    it: 'Conferma la tua email - Agentic School',
  },
  emailUnsubscribe: {
    en: 'Email preferences - Agentic School',
    de: 'E-Mail-Einstellungen - Agentic School',
    fr: 'Préférences e-mail - Agentic School',
    it: 'Preferenze email - Agentic School',
  },
}

export function noIndexTitle(key: NoIndexKey, locale: Locale): string {
  // Every locale is required by the type, so this is always a direct hit.
  return NOINDEX_TITLES[key][locale]
}
